"use client";

import { c as _c } from "react/compiler-runtime";
import { useCurrentRefinements, useRefinementList } from "react-instantsearch";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type { PlpFilterValueConfig } from "@/lib/contentful/filterGroups";
interface Props {
  values: PlpFilterValueConfig[];
}

// `useMenu` paired with the typesense-instantsearch-adapter triggered a
// disjunctive helper search on every refine; that helper's response had no
// `productType` refinement, and the adapter reconciled local state to match
// the response — clearing the user's selection on every click for facet
// values whose count was low enough that the helper "won" the reconciliation.
// `useRefinementList` skips the helper round-trip; we enforce single-select
// in the UI by clearing the existing refined value before applying the new one.
function useSelectedProductType() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      includedAttributes: ["productType"]
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    items
  } = useCurrentRefinements(t0);
  const refinement = items[0]?.refinements[0];
  if (!refinement) {
    return "";
  }
  return typeof refinement.value === "string" ? refinement.value : String(refinement.value);
}

// Select/deselect analytics live in PlpAnalyticsBridge so the event payload
// can report the post-settle `resulting_product_count` (PRD §3.5). Firing
// here would always report the pre-click count.
export function PlpProductTypeFilter(t0) {
  const $ = _c(15);
  const {
    values
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      attribute: "productType",
      operator: "and",
      limit: 50
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    items,
    refine
  } = useRefinementList(t1);
  const selectedValue = useSelectedProductType();
  let t2;
  if ($[1] !== items) {
    t2 = new Map(items.map(_temp));
    $[1] = items;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const itemsByValue = t2;
  let t3;
  if ($[3] !== refine || $[4] !== selectedValue) {
    t3 = function handleChange(next) {
      if (next === selectedValue) {
        return;
      }
      if (selectedValue) {
        refine(selectedValue);
      }
      refine(next);
    };
    $[3] = refine;
    $[4] = selectedValue;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleChange = t3;
  let t4;
  if ($[6] !== itemsByValue || $[7] !== values) {
    let t5;
    if ($[9] !== itemsByValue) {
      t5 = value => {
        const inputId = `plp-productType-${value.slug}`;
        const count = itemsByValue.get(value.productAttributeKey)?.count ?? 0;
        return <div key={value.slug} className="flex items-center gap-2"><RadioGroupItem value={value.productAttributeKey} id={inputId} /><Label htmlFor={inputId} className="flex-1 cursor-pointer text-sm font-medium">{value.label}</Label><span className="text-sm text-muted-foreground">({count})</span></div>;
      };
      $[9] = itemsByValue;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    t4 = values.map(t5);
    $[6] = itemsByValue;
    $[7] = values;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[11] !== handleChange || $[12] !== selectedValue || $[13] !== t4) {
    t5 = <RadioGroup className="flex flex-col gap-2" name="plp-filter-productType" value={selectedValue} onValueChange={handleChange}>{t4}</RadioGroup>;
    $[11] = handleChange;
    $[12] = selectedValue;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
function _temp(item) {
  return [item.value, item];
}